"use client";

import { useMemo, useState } from "react";
import { useEffect } from "react";
import Image from "next/image";
import { useIsMobile } from "@hooks/use-mobile";
import { ExchangeCarSeoCard } from "@interfaces/exchangeCarSeo.types";
import styles from "./ExchangeWhySection.module.scss";
import assetLoader from "@utils/internalImageLoder";

interface ExchangeWhySectionProps {
  title: string;
  items: ExchangeCarSeoCard[];
  hideTitle?: boolean;
}

const DEFAULT_VISIBLE_COUNT = 3;

export default function ExchangeWhySection({
  title,
  items,
  hideTitle = false,
}: ExchangeWhySectionProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const isMobile = useIsMobile();

  const shouldCollapse = !isMobile && items.length > DEFAULT_VISIBLE_COUNT;

  const visibleItems = useMemo(
    () =>
      isHydrated && shouldCollapse && !showAll
        ? items.slice(0, DEFAULT_VISIBLE_COUNT)
        : items,
    [isHydrated, items, showAll, shouldCollapse]
  );

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const gridClassName =
    visibleItems.length >= 3
      ? styles.threeColumnGrid
      : visibleItems.length === 2
        ? styles.twoColumnGrid
        : styles.oneColumnGrid;

  return (
    <div className={styles.section}>
      {!hideTitle ? <h2 className={styles.title}>{title}</h2> : null}

      <div className={`${styles.cardGrid} ${gridClassName}`}>
        {visibleItems.map((item, index) => (
          <article key={`${item.title}-${index}`} className={styles.card}>
            {item.image?.url ? (
              <div className={styles.iconWrap}>
                <Image
                  src={item.image.url}
                  alt={item.image.alt || item.title}
                  width={56}
                  height={56}
                  className={styles.icon}
                />
              </div>
            ) : null}

            <div className={styles.cardContent}>
              <h3 className={styles.cardTitle}>{item.title}</h3>
              <p className={styles.cardDescription}>{item.description}</p>
            </div>
          </article>
        ))}
      </div>

      {isHydrated && shouldCollapse ? (
        <button
          type="button"
          className={styles.showMoreButton}
          onClick={() => setShowAll((value) => !value)}
        >
          <span>{showAll ? "View less" : "View more"}</span>
          <Image
            src={`${assetLoader()}/icons/chevron_down_light.svg`}
            alt=""
            width={20}
            height={20}
            className={`${styles.chevron} ${showAll ? styles.chevronUp : ""}`}
            aria-hidden="true"
          />
        </button>
      ) : null}
    </div>
  );
}
